import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import SearchParams from "simplydo/core/searchParams";
import type { OpenAPI } from "simplydo/interfaces";
import { Button, Container, Divider, Loader, Message } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";

import InviteCard from "components/lib/Invitations/InviteCard";
import type { HomeInvitation } from "components/lib/Invitations/types";

export const Invitation = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const params = useMemo(
    () => new SearchParams(location.search),
    [location.search],
  );
  const token = useMemo(() => params.get("invite_token"), [params]);
  const [invitation, setInvitation] = useState<HomeInvitation | null>(null);
  const invitationEmailParam = useMemo(() => {
    if (invitation?.invitationType === "email") {
      return `&email=${encodeURIComponent(invitation.invitee)}`;
    }
    return "";
  }, [invitation]);
  const [loading, setLoading] = useState(true);
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const reloadUser = useUserReload();

  useEffect(() => {
    if (!token) {
      return;
    }

    api.invitations.findByToken(
      token,
      (data: OpenAPI.GET<"/invitations/find_by_token">["response"]) => {
        const userEmail = user?.emails?.[0]?.address ?? "";
        const matchesCurrentUserEmail =
          data.invitation.invitationType === "email" &&
          data.invitation.invitee === userEmail;
        const matchesCurrentUserAlias =
          data.invitation.invitationType === "email" &&
          data.invitation.alias === userEmail;

        // Automatically redirect the user to the relevant page if they are logged in and the invitation is for them
        if (
          data.invitation.acceptedBy &&
          (matchesCurrentUserEmail || matchesCurrentUserAlias)
        ) {
          switch (data.invitation.forType) {
            case "group":
            case "requestJoinGroup":
              navigate(`/groups/${data.invitation.forId}`);
              break;
            case "idea":
            case "requestJoinIdea":
              navigate(`/ideas/${data.invitation.forId}`);
              break;
            case "projectInvited":
              navigate(
                `/challenges/${(data.invitation as HomeInvitation).idea?.challenge}/ideas?queryView=board&idea=${data.invitation.forId}`,
              );
              break;
            case "ideaAssessor":
              navigate(
                `/challenges/${data.invitation.forId}/assessments/assigned`,
              );
              break;
            default:
              navigate(`/invitations`);
          }
        }
        setInvitation(data.invitation as HomeInvitation);

        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );
  }, [token, user, navigate]);

  const acceptInvite = useCallback(
    (
      invitation: HomeInvitation,
      onSuccess: (i: HomeInvitation) => void,
      onError: (i: HomeInvitation) => void,
    ) => {
      if (!token) {
        return;
      }
      api.invitations.acceptToken(
        token,
        () => {
          setInvitation(null);
          onSuccess(invitation);
          api.setQueryData(api.invitations.get, [], (prev) => ({
            ...prev,
            invitations: (prev?.invitations || []).filter(
              (i) => i._id !== invitation._id,
            ),
          }));
          reloadUser(() => {
            switch (invitation.forType) {
              case "group":
              case "requestJoinGroup":
                api.invalidateQueries(api.groups.get, [invitation.forId]);
                api.invalidateQueries(api.pages.getAll, [
                  "group",
                  invitation.forId,
                ]);
                api.invalidateQueries(api.discussions.getAll, [
                  "group",
                  invitation.forId,
                ]);
                navigate(`/groups/${invitation.forId}`);
                break;
              case "idea":
              case "requestJoinIdea":
                navigate(`/ideas/${invitation.forId}`);
                break;
              case "projectInvited":
                navigate(
                  `/challenges/${invitation.idea?.challenge}/ideas?queryView=board&idea=${invitation.forId}`,
                );
                break;
              case "ideaAssessor":
                navigate(
                  `/challenges/${invitation.forId}/assessments/assigned`,
                );
                break;
              case "ideaBusinessProfile":
                navigate(`/`);
                break;
              default:
                navigate(`/invitations`);
            }
          });
        },
        () => onError(invitation),
      );
    },
    [token, navigate, reloadUser],
  );

  return (
    <div>
      {(!token || !invitation) && (
        <>
          <h1>Invitations</h1>
          <Divider />
        </>
      )}
      <Container style={{ paddingBottom: 5 }}>
        {token && invitation ?
          <div>
            <div>
              <Container style={{ paddingBottom: 5 }}>
                {loading ?
                  <Loader active inverted content="Loading" />
                : <InviteCard
                    cardView={false}
                    acceptInvite={acceptInvite}
                    rejectInvite={() => {}} // unused for token invites
                    removeInvite={() => {}} // unused for token invites
                    invitation={invitation}
                  />
                }
              </Container>
            </div>
            {user ? null : (
              <>
                <Message style={{ marginTop: 0 }} info>
                  {t("invitation.loginToAccept")}
                </Message>
                <Button
                  style={{ float: "right" }}
                  primary
                  as={Link}
                  to={`/login?then=${encodeURIComponent(`/invitations?invite_token=${token}`)}${invitationEmailParam}`}
                >
                  {t("entry.login.loginOrSignUp")}
                </Button>
              </>
            )}
          </div>
        : <div>
            {token ?
              <Message error>
                You followed a link to an invitation that has expired or was
                already accepted. If you think this is a mistake or if you need
                help, please contact the person who sent you the invitation.
              </Message>
            : null}
            {user ?
              <Button primary as={Link} to={`/`} style={{ float: "right" }}>
                {t("generic.backToApplication")}
              </Button>
            : <>
                <Message info>
                  Log in to view your account or sign up below.
                </Message>
                <Button
                  primary
                  as={Link}
                  to={`/login`}
                  style={{ float: "right" }}
                >
                  {t("entry.login.loginOrSignUp")}
                </Button>
              </>
            }
          </div>
        }
      </Container>
    </div>
  );
};
